import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Loader2, Star } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeaveReview() {
  const [params] = useSearchParams();
  const sourceId = params.get("source") || ""; // case_id or member_id, for admin attribution
  const [form, setForm] = useState({
    first_name: "",
    location: "",
    comment: "",
    consent: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please confirm consent to display your review.");
      return;
    }
    if (form.comment.trim().length < 20) {
      toast.error("Just a couple of sentences please — at least 20 characters.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/reviews/submit`, {
        first_name: form.first_name.trim(),
        location: form.location.trim(),
        comment: form.comment.trim(),
        source_id: sourceId,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div data-testid="review-thanks-page">
        <SEO title="Thank You — Park Home Support" description="Thanks for your feedback." />
        <section className="section-pad bg-[#F7F4ED]">
          <div className="container-phs max-w-2xl">
            <div className="card-default text-center">
              <div className="w-14 h-14 bg-[#064E2E] text-white rounded-2xl flex items-center justify-center mx-auto">
                <CheckCircle2 size={28} />
              </div>
              <h1 className="font-display text-3xl sm:text-4xl mt-5 text-[#071A33]">
                Thank you — that&apos;s genuinely helpful.
              </h1>
              <p className="mt-4 text-[#475569] text-lg leading-relaxed">
                We&apos;ll review what you&apos;ve sent and, if it helps other park home owners, it may appear on the site (first name and optional location only — never email or contact details).
              </p>
              <div className="mt-7 flex flex-wrap gap-3 justify-center">
                <Link to="/" className="btn-primary" data-testid="review-thanks-home-btn">
                  Back to Home <ArrowRight size={16} />
                </Link>
                <Link to="/quote-review" className="btn-outline">
                  Send Another Quote
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="leave-review-page">
      <SEO
        title="Leave a Review | Park Home Support"
        description="If Park Home Support helped you avoid getting something wrong, we'd love to hear about it."
      />
      <section className="section-pad bg-[#F7F4ED]">
        <div className="container-phs max-w-2xl">
          <span className="eyebrow">Your feedback</span>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight text-[#071A33]">
            Quick one — was this helpful?
          </h1>
          <p className="mt-5 text-[#475569] text-lg">
            If Park Home Support helped you avoid getting something wrong, we&apos;d genuinely love to hear about it. A couple of sentences is plenty.
          </p>

          <form onSubmit={onSubmit} className="card-default mt-8 space-y-5" data-testid="review-form">
            <div className="flex items-center gap-2 text-[#B88A2A]">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={20} fill="#B88A2A" strokeWidth={0} />
              ))}
              <span className="text-xs text-[#475569] ml-1">Optional — leave a star rating in your words.</span>
            </div>

            <div>
              <label className="field-label">First name</label>
              <input
                className="field-input"
                value={form.first_name}
                onChange={(e) => update("first_name", e.target.value)}
                required
                data-testid="review-first-name"
                placeholder="e.g. Dean"
              />
            </div>

            <div>
              <label className="field-label">Location <span className="text-[#94A3B8] font-normal">(optional)</span></label>
              <input
                className="field-input"
                value={form.location}
                onChange={(e) => update("location", e.target.value)}
                data-testid="review-location"
                placeholder="e.g. Bournemouth"
              />
            </div>

            <div>
              <label className="field-label">Your feedback</label>
              <textarea
                className="field-input min-h-[140px]"
                value={form.comment}
                onChange={(e) => update("comment", e.target.value)}
                required
                minLength={20}
                data-testid="review-comment"
                placeholder="What did Park Home Support help you with?"
              />
              <p className="text-xs text-[#94A3B8] mt-1">{form.comment.length}/500 characters</p>
            </div>

            <label className="flex items-start gap-3 text-sm text-[#475569]">
              <input
                type="checkbox"
                className="mt-1 accent-[#064E2E] w-4 h-4"
                checked={form.consent}
                onChange={(e) => update("consent", e.target.checked)}
                data-testid="review-consent"
              />
              <span>
                I&apos;m happy for my first name and (optionally) my location to be displayed alongside this feedback on the Park Home Support website.
              </span>
            </label>

            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full sm:w-auto"
              data-testid="review-submit-btn"
            >
              {busy ? (
                <>
                  <Loader2 className="animate-spin" size={16} /> Sending…
                </>
              ) : (
                <>
                  Send Feedback <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
